import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import apiClient from '../../../lib/client';

const GuideContext = createContext(null);

const STORAGE_KEY_PREFIX = 'votewilco:selections:';
const ANON_TOKEN_KEY = 'anon_session_token';
const ACCESS_TOKEN_KEY = 'accessToken';

function parseJwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function getStoredSession() {
  const full = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (full) {
    const payload = parseJwtPayload(full);
    if (payload && payload.exp * 1000 > Date.now()) {
      return { token: full, isAnonymous: false, userId: payload.userId };
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
  const anon = localStorage.getItem(ANON_TOKEN_KEY);
  if (anon) {
    const payload = parseJwtPayload(anon);
    if (payload && payload.exp * 1000 > Date.now()) {
      return { token: anon, isAnonymous: true, userId: payload.userId };
    }
    localStorage.removeItem(ANON_TOKEN_KEY);
  }
  return null;
}

function loadSelections(electionId) {
  if (!electionId) return {};
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${electionId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSelections(electionId, selections) {
  if (!electionId) return;
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${electionId}`, JSON.stringify(selections));
}

export function GuideProvider({ children }) {
  const [address, setAddress] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [selections, setSelections] = useState({});
  const [userId, setUserId] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const sessionInitialized = useRef(false);

  // On mount: ensure a session token exists (anonymous or full)
  useEffect(() => {
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    const existing = getStoredSession();
    if (existing) {
      setUserId(existing.userId);
      setIsAnonymous(existing.isAnonymous);
      return;
    }

    apiClient
      .post('/auth/anonymous-session')
      .then(({ data }) => {
        if (data.ok && data.accessToken) {
          localStorage.setItem(ANON_TOKEN_KEY, data.accessToken);
          const payload = parseJwtPayload(data.accessToken);
          if (payload) {
            setUserId(payload.userId);
            setIsAnonymous(true);
          }
        }
      })
      .catch(() => {});
  }, []);

  // When a new election is loaded, hydrate selections from localStorage
  useEffect(() => {
    const electionId = lookupResult?.election?.id;
    if (electionId) {
      setSelections(loadSelections(electionId));
    }
  }, [lookupResult?.election?.id]);

  const setLookupResultAndAddress = useCallback((addr, result) => {
    setAddress(addr);
    setLookupResult(result);
  }, []);

  const selectCandidate = useCallback(
    (contestId, candidateId) => {
      const electionId = lookupResult?.election?.id;
      setSelections((prev) => {
        const next = { ...prev, [contestId]: candidateId };
        saveSelections(electionId, next);
        return next;
      });
    },
    [lookupResult?.election?.id]
  );

  const clearSelection = useCallback(
    (contestId) => {
      const electionId = lookupResult?.election?.id;
      setSelections((prev) => {
        const next = { ...prev };
        delete next[contestId];
        saveSelections(electionId, next);
        return next;
      });
    },
    [lookupResult?.election?.id]
  );

  const totalCount = lookupResult?.ballot?.totalCount ?? 0;
  const selectedCount = Object.keys(selections).length;
  const isAuthenticated = !!userId && !isAnonymous;

  const value = useMemo(
    () => ({
      address,
      lookupResult,
      selections,
      totalCount,
      selectedCount,
      userId,
      isAnonymous,
      isAuthenticated,
      setLookupResultAndAddress,
      selectCandidate,
      clearSelection,
    }),
    [
      address,
      lookupResult,
      selections,
      totalCount,
      selectedCount,
      userId,
      isAnonymous,
      isAuthenticated,
      setLookupResultAndAddress,
      selectCandidate,
      clearSelection,
    ]
  );

  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}

export function useGuide() {
  const ctx = useContext(GuideContext);
  if (!ctx) throw new Error('useGuide must be used within a GuideProvider');
  return ctx;
}
